import capitalize from 'lodash.capitalize';

import { Option, Taxonomy } from '../features/rewards/api/hooks/types';

// Reference: https://stackoverflow.com/questions/63491179/sorting-array-of-mixed-clothing-sizes-in-javascript
const SIZE_WEIGHTS: any = {
	'one size': 1,
	xxs: 2,
	'2xs': 3,
	'double extra small': 4,
	xs: 5,
	'extra small': 6,
	s: 7,
	sm: 8,
	small: 9,
	m: 10,
	md: 11,
	medium: 12,
	l: 13,
	lg: 14,
	large: 15,
	xl: 16,
	'extra large': 17,
	'2xl': 18,
	xxl: 19,
	'double extra large': 20,
	'3xl': 21,
	xxxl: 22,
	'triple extra large': 23,
	'4xl': 24,
	xxxxl: 25,
	'quadruple extra large': 26,
};

// Sort weight for a single size token. Unknown sizes sort after all known ones (weight 100), keeping
// their relative order stable when combined with a stable sort.
export const getSizeWeight = (size: string): number => SIZE_WEIGHTS[size?.trim().toLowerCase()] || 100;

export const orderByColorThenSize = (option1: Option, option2: Option) => {
	const color1 = option1.color?.trim().toLowerCase();
	const color2 = option2.color?.trim().toLowerCase();
	const size1 = option1.size?.trim().toLowerCase();
	const size2 = option2.size?.trim().toLowerCase();

	// First order by color if it exists
	if (color1 && color2) {
		if (color1 > color2) {
			return 1;
		} else if (color1 < color2) {
			return -1;
		}
	}
	// Then by size if it exists
	if (size1 && size2) {
		const size1Weight = SIZE_WEIGHTS[size1] || 100;
		const size2Weight = SIZE_WEIGHTS[size2] || 100;

		return size1Weight - size2Weight;
	}

	// Default - dont sort
	return 0;
};

/**
 * Axis keys whose values are not option values and so keep the provider's casing verbatim:
 * - "size" values are standardized codes ("XL", "2XL", "OS").
 * - "takeYourPick" values name the gift itself, so casing would mangle titles like "Ember Mug² - Black".
 * - "amount" is what gift cards and donations report, where the value is a denomination title.
 */
const VERBATIM_AXIS_KEYS = new Set(['amount', 'size', 'takeYourPick']);

/**
 * Display label for an option value, whether it's a matrix axis value or the single option a variant
 * stands for. Providers send these in whatever casing they please ("clear", "BLACK"), and we show
 * them in sentence case — except the axes in {@link VERBATIM_AXIS_KEYS}.
 *
 * Values are trimmed first because provider values arrive with stray whitespace (see the trimming in
 * {@link getSizeWeight}), which would otherwise leave the leading space as the "first character".
 */
export const formatAxisValueLabel = (axisKey: string, value: string): string => (VERBATIM_AXIS_KEYS.has(axisKey) ? value : capitalize(value.trim()));

export const getTaxonomyDisplay = (taxonomy: Taxonomy) => {
	const { color, size } = taxonomy;
	let label = '';
	if (color && size) label = `${color} ${size}`;
	else if (color) label = `${color}`;
	else if (size) label = `${size}`;

	return label;
};
